import React from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import * as Immutable from 'immutable';

// Util
import measurementsConnect from '../../components/measurementsStore/measurementsConnect';
import { transformMeasurementsInPlace } from '../../components/measurementsStore/elementMeasurements/utils/measureElementZoomUtils';
import { addMargins, asRect, getBoundingRect } from '../../../common/maths/geometry/rect';
import animateScrollIntoView from '../../utils/animation/animateScrollIntoView';
import getParentScrollableSectionForElementId from '../../utils/dom/getParentScrollableSectionForElementId';

// Selectors
import { elementFilterDataSelector } from './elementFilterSelector';
import { getMeasurementsMap } from '../../components/measurementsStore/elementMeasurements/elementMeasurementsSelector';
import { canvasZoomStateSelector } from '../../canvas/store/canvasZoomSelector';
import popupOpenSelector from '../../components/popupPanel/popupOpenSelector';

// Constants
import { PopupIds } from '../../components/popupPanel/popupConstants';

// Types
import {
    ElementMeasurement,
    ImElementMeasurement,
} from '../../components/measurementsStore/elementMeasurements/elementMeasurementsTypes';
import { setSelectedElements } from '../selection/selectionActions';
import { getMany } from '../../../common/utils/immutableHelper';
import usePrevious from '../../utils/react/usePrevious';
import { ElementFilterDataMap } from './dataBuilder/elementFilterDataTypes';

const mapStateToProps = createStructuredSelector({
    filterData: elementFilterDataSelector,
    zoomState: canvasZoomStateSelector,
    searchPopupOpen: popupOpenSelector(PopupIds.SEARCH),
});

const mapDispatchToProps = (dispatch: Function) => ({
    dispatchSelectElements: (elementIds: string[]) => {
        dispatch(
            setSelectedElements({
                ids: elementIds,
                rangeAnchors: null,
                transactionId: -1,
            }),
        );
    },
});

const mapMeasurementsStateToProps = createStructuredSelector({
    elementMeasurements: getMeasurementsMap,
});

const connectState = connect(mapStateToProps, mapDispatchToProps);
const connectMeasurements = measurementsConnect(mapMeasurementsStateToProps);

type Props = ConnectedProps<typeof connectState> & {
    elementMeasurements: Immutable.Map<string, ImElementMeasurement>;
};

const WORKSPACE_BOUNDS_MARGIN_RECT = asRect({
    top: 200,
    left: 200,
    bottom: 300,
    right: 200,
});

const SEARCH_POPUP_OPEN_MARGIN_RECT = addMargins(
    {
        top: 0,
        left: 0,
        bottom: 0,
        right: 400,
    },
    WORKSPACE_BOUNDS_MARGIN_RECT,
);

const getFilterFocusIds = (filterData: ElementFilterDataMap | null | undefined): string[] => {
    if (!filterData) return [];

    return Object.values(filterData)
        .filter((filterOb) => filterOb.focus)
        .map((filterOb) => filterOb.elementId);
};

export const ElementFilterFocusedManager = ({
    filterData,
    elementMeasurements,
    zoomState,
    searchPopupOpen,
    dispatchSelectElements,
}: Props): null => {
    const prevFilterData = usePrevious(filterData);

    const filterFocusIds = getFilterFocusIds(filterData);
    const prevFilterFocusIds = getFilterFocusIds(prevFilterData);

    // Effect: select the focused elements
    React.useEffect(() => {
        if (filterFocusIds.length > 0) {
            dispatchSelectElements(filterFocusIds);
        } else if (filterFocusIds.length <= 0 && prevFilterFocusIds.length > 0) {
            dispatchSelectElements([]);
        }
    }, [filterFocusIds, prevFilterFocusIds]);

    // Effect: scroll the focused elements into view
    React.useEffect(() => {
        if (filterFocusIds.length <= 0) return;

        const focusRects = Object.values(getMany(filterFocusIds, elementMeasurements).toJS()) as ElementMeasurement[];
        if (focusRects.length <= 0) return;

        const boundingFocusRect = getBoundingRect(focusRects);
        if (!boundingFocusRect || boundingFocusRect.width === 0 || boundingFocusRect.height === 0) return;

        // if the search popup is open, we need to add a margin to the right of the workspace bounds
        // so the element is not hidden behind the popup
        const workspaceBoundsRect = searchPopupOpen ? SEARCH_POPUP_OPEN_MARGIN_RECT : WORKSPACE_BOUNDS_MARGIN_RECT;

        const focusRectWithMargins = addMargins(
            workspaceBoundsRect,
            transformMeasurementsInPlace(boundingFocusRect, zoomState),
        );

        const parent = getParentScrollableSectionForElementId(filterFocusIds[0]);

        animateScrollIntoView({
            scrollableParent: parent,
            targetRect: focusRectWithMargins,
            viewportOptions: undefined,
            interpolationFactor: 0.2,
        });
    }, [filterFocusIds, elementMeasurements]);

    return null;
};

export default connectMeasurements(connectState(ElementFilterFocusedManager));
